import React, { useState } from 'react'
import {
  Alert,
  App as AntdApp,
  Button,
  Input,
  Result,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd'
import { supabaseClient } from '../../lib/supabaseClient'
import { ListPage, SectionCard, StatStrip, type Stat } from '../../theme/listkit'
import { brand } from '../../theme/brand'
import { Image as ImageIcon, UsersRound, Bell, Send } from '../../components/icons'
import { fnErrorMessage } from '../../lib/fnError'

/**
 * The refusals the function reports in `reason`, as far as this page is concerned.
 *
 * THE SERVER'S LIST IS LONGER, AND THAT IS FINE — but only because of where the
 * others surface. `BACKFILL_REASONS` in
 * `backend/supabase/functions/_shared/photoTagBackfillPlan.ts` is the contract
 * and currently holds eleven values. They split into three groups:
 *
 *   • Never reaches this page as a 200-refusal: `unauthenticated` and
 *     `not_admin` come back as 401/403 and are thrown by supabase-js, so they
 *     land in the catch and go through `fnErrorMessage`.
 *   • Arrives as a 4xx with an actionable `hint`: `confirm_required` (428) and
 *     `over_ceiling` (409). `fnErrorMessage` joins error + hint, so the operator
 *     gets the server's own words — including the exact phrase to re-send.
 *   • Arrives as HTTP 200 with `ok: false`, i.e. through `refusalMessage`:
 *     the three below, plus `bad_cutoff`, `forward_path_dark`, `read_failed`
 *     and `scan_too_large`. Those four all carry BOTH `error` and `hint`, which
 *     `refusalMessage` joins, so they degrade to the server's own actionable
 *     wording rather than to the generic fallback.
 *
 * So this union is deliberately the set with BESPOKE operator copy, not the set
 * the server can send. `refusalMessage` prefers the server's `error`/`hint`
 * whenever they are present and only reaches into `REFUSAL_COPY` for these
 * three, where a better-worded, admin-specific instruction exists. Adding a
 * reason here is an improvement, never a prerequisite — which is what stops the
 * two lists drifting into a broken page.
 */
export type BackfillRefusal = 'flag_off' | 'settings_read_degraded' | 'no_recipients'

export interface BackfillSkip {
  user_id: string
  reason: string
}
export interface BackfillSampleRow {
  user_id: string
  name: string
  tags: number
  has_token: boolean
}
export interface BackfillFailure {
  user_id: string
  step: string
  error: string
}

/** Shape the `photo-tag-backfill` Edge Function returns on a 2xx — dry run or live. */
export interface PhotoTagBackfillResult {
  ok: boolean
  dry_run: boolean
  reason?: BackfillRefusal
  error?: string
  hint?: string
  scanned: number
  members: number
  notified: number
  pushed: number
  skipped: BackfillSkip[]
  sample: BackfillSampleRow[]
  failures: BackfillFailure[]
  note?: string
}

/**
 * The phrase the function requires, bound to the member count the dry run
 * reported. Mirrors `confirmPhrase()` in
 * `backend/supabase/functions/_shared/photoTagBackfillPlan.ts` — the two must be
 * byte-identical or every live run is refused, which is what
 * `src/__tests__/photoTagBackfillParity.test.ts` pins against the real server
 * source. Exported as a pure function so the phrase can be tested without
 * driving the page.
 */
export const confirmPhraseFor = (members: number) => `NOTIFY ${members} MEMBERS`

/**
 * Operator copy per refusal. These are NOT decoration: each one names a
 * different thing the admin has to go and fix, and the wrong one on screen sends
 * them to the wrong place. `flag_off` is a switch in Feature Flags;
 * `settings_read_degraded` is a database read that failed, where the correct
 * behaviour is to wait and retry rather than push; `no_recipients` means the
 * scan found nobody — which is a failure, not a send. That last one is the
 * lesson from `send_push`, which used to report success at zero recipients, so
 * five broadcasts in one day looked like they had worked while nobody had a
 * registered device.
 */
export const REFUSAL_COPY: Record<BackfillRefusal, string> = {
  flag_off:
    'Nothing was sent — push notifications are switched off in Feature Flags (push_notifications_enabled). Turn that on, then run the preview again.',
  settings_read_degraded:
    'Nothing was sent — the member push-preference read failed, so the run refused rather than risk pushing to members who opted out. Retry once the database read is healthy.',
  no_recipients:
    'Nothing was sent — no tagged member was eligible. Zero recipients is a failure, not a send: the skipped list below says why each one was passed over.',
}

/**
 * The one line to show when the payload says the run refused.
 *
 * Pure and separately tested because this is the whole of the page's judgement:
 * a refusal arrives as **HTTP 200** with `ok:false`, so anything branching on
 * the HTTP status sees a success and cheerfully says so. Returns null when the
 * payload reports a run that actually happened.
 *
 * `ok !== true` is read here as "refused, nothing was sent", which is exactly
 * what the function means by it — see WHAT `ok` MEANS above. A completed run
 * carrying `failures` is NOT a refusal and must return null from here, or the
 * page tells an operator nothing was sent about members who were notified, and
 * the recovery instinct after that lie is to re-run.
 */
export function refusalMessage(
  r: Pick<PhotoTagBackfillResult, 'ok' | 'reason' | 'error' | 'hint'> | null | undefined,
): string | null {
  if (!r || r.ok === true) return null
  if (r.reason && REFUSAL_COPY[r.reason]) return REFUSAL_COPY[r.reason]
  // An unknown or absent reason still must not read as success. Prefer whatever
  // the server said over a generic line, but never show nothing at all.
  const parts = [r.error, r.hint].filter(
    (v): v is string => typeof v === 'string' && v.trim() !== '',
  )
  return parts.length ? parts.join(' — ') : 'The run refused and nothing was sent.'
}

/**
 * `fnErrorMessage`, plus the `hint` the server sent with it.
 *
 * WHY THIS WRAPPER EXISTS. `fnErrorMessage` returns `body.error` and DISCARDS
 * `body.hint` (`lib/fnError.ts`). For this function that drops the actionable
 * half of both status-borne refusals: the 428 phrase mismatch sends
 * `error: "Confirmation required for a live backfill"` with
 * `hint: "Re-send with confirm set to exactly: NOTIFY 3 MEMBERS"`, and the 409
 * ceiling sends the "check the selection before raising MAX_MEMBERS_CEILING"
 * hint (`photoTagBackfillPlan.decideBackfill`). The page's OWN payload path
 * joins error + hint (`refusalMessage`), so without this the same refusal told
 * the operator two different amounts depending on which road it travelled.
 *
 * WHY NOT `edgeErrorMessage`, WHICH ALREADY JOINS THEM. It parses the body as
 * JSON and has no 404 branch, so the likeliest first failure of a brand-new
 * function — the API gateway's `text/plain` "Function not found" — would come
 * back as the opaque "non-2xx status code". WHY NOT FIX `fnErrorMessage`: eight
 * other admin pages read it and its contract is "the message", not "the
 * message and the hint".
 *
 * WHY THE BODY IS REPLAYED rather than read twice: `context` is a real
 * `Response`, whose body can be consumed exactly once. Reading it here and then
 * handing `fnErrorMessage` the consumed Response would silently degrade every
 * message to the opaque wrapper, so it is handed the bytes instead — which also
 * keeps fnErrorMessage the single implementation of the 404/plain-text rules
 * rather than copying them here.
 */
export async function invokeErrorMessage(e: unknown): Promise<string> {
  const err = e as { message?: string; context?: Response } | null
  const ctx = err?.context
  if (!ctx || typeof ctx.text !== 'function') return fnErrorMessage(e)

  let raw = ''
  try {
    raw = await ctx.text()
  } catch {
    /* body already consumed or unreadable — fnErrorMessage handles the empty case */
  }
  const base = await fnErrorMessage({
    message: err?.message,
    context: { status: ctx.status, text: async () => raw } as unknown as Response,
  })

  let hint: string | undefined
  try {
    const body = JSON.parse(raw.trim()) as { hint?: unknown }
    if (typeof body?.hint === 'string' && body.hint.trim() !== '') hint = body.hint.trim()
  } catch {
    /* non-JSON body (e.g. "Function not found") — there is no hint to add */
  }
  // `base.includes(hint)` guards the day fnErrorMessage learns to join them
  // itself: appending a hint that is already in the string would print it twice.
  if (!hint || base.includes(hint)) return base
  return `${base} — ${hint}`
}

/**
 * Photo Tag Backfill — the one-time catch-up for the `photo.tagged`
 * notification.
 *
 * WHY THIS PAGE EXISTS. The `photo.tagged` rule has sat in `notification_rules`
 * marked enabled since `20260623130000_notification_emitters.sql`, and it has
 * never once fired: its trigger was created inside a DO block guarded on
 * `supabase_functions.http_request` (an extension this project does not have),
 * its `app.rule_engine.*` GUCs are unsettable on hosted Supabase, and
 * `feed-activity` had no `config.toml` entry so it ran at `verify_jwt=true`
 * while the trigger sent no Authorization header. Three independent reasons for
 * the same silence. So every member already tagged in a photo was never told,
 * and repairing the emitter only helps the NEXT tag. This page tells the members
 * who were tagged before the repair.
 *
 * THE BRAKES ARE DELIBERATELY DOUBLED. The live run is unreachable until a dry
 * run has reported a roster, and stays disabled until the operator types the
 * exact count-bound phrase — both of which the Edge Function re-checks against
 * the body it received. This is a second lock on the same door, not the only
 * one: the function is callable without this page, so the UI mirroring its
 * brakes is a courtesy to the operator, never the enforcement point.
 *
 * WHAT `ok` MEANS, AND WHAT IT DOES NOT. `ok` answers ONE question: was the run
 * accepted and executed? Every `ok:false` the function returns is a refusal that
 * wrote nothing, and carries a `reason` and/or an `error` saying so
 * (`photo-tag-backfill/index.ts` — the four `ok:false` returns, and the comment
 * above its completion return). A run that DID execute always comes back
 * `ok:true`; a partial failure is expressed by a non-empty `failures` array
 * beside real `notified`/`pushed` counts. That field used to be
 * `failures.length === 0`, which made a completed-with-one-failure run
 * indistinguishable from a refusal, so this page rendered "nothing was sent"
 * next to a stat strip reading Notified 3. This page therefore reads `ok` for
 * the refusal question and `failures` for the how-did-it-go question, and never
 * either for the other. `src/__tests__/photoTagBackfillParity.test.ts` pins that
 * split against the server's own source.
 *
 * WHY IT DOES NOT RE-SCAN AFTER A RUN, unlike `purge_test_users`. Not because a
 * second run is dangerous — the server makes it a near-no-op (see the copy in
 * the info Alert, and `photoTagBackfillPlan.ts`'s own "provable no-op"). Because
 * the live button must never be armed by anything except an operator reading a
 * fresh roster: an automatic re-scan would put a count back on screen that
 * nobody looked at, and the count-bound phrase is only a brake while the count
 * came from a preview somebody actually read. A live run therefore clears the
 * preview, and re-running (the correct recovery for a run that reported
 * failures) starts with a deliberate new preview.
 *
 * REFUSALS ARRIVE BY TWO ROADS, and both are handled. A refusal the function
 * decided on arrives as **HTTP 200** with `ok:false` and a `reason` — so
 * branching on the HTTP status would read it as a success. The guardrails in
 * `photoTagBackfillPlan.ts` refuse with a status instead (428 for a
 * confirm-phrase mismatch, 409 for a live run that would notify nobody), which
 * supabase-js turns into a thrown error; those surface through
 * `invokeErrorMessage` — `fnErrorMessage` plus the server's `hint`, which is
 * where those two refusals keep the half the operator can act on.
 *
 * HONESTY ABOUT `pushed`. It counts push messages POSTed to Expo, not messages
 * delivered. Nothing in this system writes `push_tickets` and nothing polls
 * receipts, so a POSTed message can still be dropped downstream without a
 * trace. The UI says exactly that rather than implying delivery.
 */
export function PhotoTagBackfillPage() {
  const { message, modal } = AntdApp.useApp()
  const [result, setResult] = useState<PhotoTagBackfillResult | null>(null)
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState<'preview' | 'live' | null>(null)
  /** The transport/gateway failure, kept on screen — a toast is gone in seconds. */
  const [invokeError, setInvokeError] = useState<string | null>(null)

  const call = async (live: boolean) => {
    setBusy(live ? 'live' : 'preview')
    setInvokeError(null)
    try {
      const { data, error } = await supabaseClient.functions.invoke<PhotoTagBackfillResult>(
        'photo-tag-backfill',
        { body: live ? { dry_run: false, confirm } : { dry_run: true } },
      )
      if (error) throw error
      setResult(data ?? null)
      // Always retype. The phrase is bound to the count in the CURRENT payload,
      // so a phrase typed against the previous one must never carry over.
      setConfirm('')

      const refusal = refusalMessage(data)
      if (refusal) {
        message.warning(refusal, 8)
        return
      }
      if (live) {
        const notified = Number(data?.notified ?? 0)
        const pushed = Number(data?.pushed ?? 0)
        message.success(
          `Notified ${notified} member${notified === 1 ? '' : 's'} — ${pushed} push message${
            pushed === 1 ? '' : 's'
          } POSTed to Expo`,
        )
      } else {
        const members = Number(data?.members ?? 0)
        message.info(`${members} member${members === 1 ? '' : 's'} would be notified`)
      }
    } catch (e) {
      // invokeErrorMessage: fnErrorMessage's 404 handling (this function is
      // brand new and the likeliest first failure is the API gateway's
      // text/plain "Function not found") PLUS the server's hint, which is where
      // the 428 keeps the exact phrase to re-send. See its header.
      const msg = await invokeErrorMessage(e)
      setInvokeError(msg)
      message.error(msg, 8)
      // A live run that threw may have notified some members before failing, and
      // there is no way to tell from here. Drop the preview so the button is
      // unreachable until someone deliberately re-scans, rather than leaving it
      // armed with a count that is now a guess.
      if (live) setResult(null)
    } finally {
      setBusy(null)
    }
  }

  // The live run is armed off the CURRENT payload only, and only off a dry run
  // that actually completed. `ok === true` is DEFENCE IN DEPTH and is currently
  // redundant — every 200-refusal in index.ts spreads `...emptyCounts()` first,
  // so a refused dry run reports members: 0 and the `members > 0` clause alone
  // would hold. It stays because the redundancy is one server edit deep: the
  // live-road `settings_read_degraded` refusal already echoes `members`, and the
  // day a dry-run refusal does the same, arming on the count alone would offer
  // a send the function has already said it will refuse.
  const preview = result && result.dry_run === true && result.ok === true ? result : null
  const members = preview?.members ?? 0
  const expected = confirmPhraseFor(members)
  const canRun = preview !== null && members > 0 && confirm === expected && busy === null

  const refusal = refusalMessage(result)
  const failures = result?.failures ?? []
  const skipped = result?.skipped ?? []
  const sample = result?.sample ?? []
  /** True once this payload describes a send that HAPPENED, not one being previewed. */
  const ran = result?.dry_run === false && result.ok === true

  // Failures arrive in TWO shapes and counting them as one misreports an
  // outage. A per-member failure names the member; a WHOLE-STEP failure carries
  // `user_id: ''` — index.ts pushes `{user_id:'', step:'push'}` when the entire
  // Expo fan-out throws and `{user_id:'', step:'dispatch_log'}` when the dedup
  // ledger write fails for everyone it just sent to. Rendered as a member row,
  // a total push outage read as "1 member failed" beside an empty code element,
  // naming nobody.
  const memberFailures = failures.filter((f) => f.user_id !== '')
  const stepFailures = failures.filter((f) => f.user_id === '')
  const failureHeadline = [
    memberFailures.length > 0 &&
      `${memberFailures.length} member${memberFailures.length === 1 ? '' : 's'} failed`,
    stepFailures.length > 0 &&
      `${stepFailures.length} whole-run step${stepFailures.length === 1 ? '' : 's'} failed`,
  ]
    .filter((s): s is string => typeof s === 'string')
    .join(' · ')
  /** Members named in `failures`, so the roster cannot show them as told. */
  const failedMembers = new Set(memberFailures.map((f) => f.user_id))

  const stats: Stat[] = result
    ? [
        { label: 'Tags scanned', value: result.scanned, icon: ImageIcon, tone: 'jaguar' },
        { label: 'Members', value: result.members, icon: UsersRound, tone: 'jaguar' },
        { label: 'Notified', value: result.notified, icon: Bell, tone: 'gold' },
        { label: 'POSTed to Expo', value: result.pushed, icon: Send, tone: 'neutral' },
      ]
    : []

  // `modal` from useApp(), not the static Modal.confirm: the static API renders
  // outside the App/ConfigProvider context, so it loses the admin theme and, on
  // React 19, does not reliably mount at all.
  const runLive = () => {
    modal.confirm({
      title: `Notify ${members} member${members === 1 ? '' : 's'} about their photo tags?`,
      okText: `Send ${members} notification${members === 1 ? '' : 's'}`,
      okButtonProps: { danger: true },
      cancelText: 'Not yet',
      content: (
        <span>
          Each tagged member gets an in-app notification, plus a push if they have a registered
          device. Members without one get the in-app notification only. A notification cannot be
          recalled once it is out — but the run is de-duplicated per member on the server, so if
          this one reports failures, previewing and running again retries only the members it
          missed.
        </span>
      ),
      onOk: () => call(true),
    })
  }

  return (
    <ListPage
      intro={{
        eyebrow: 'System · Maintenance',
        description:
          'One-time catch-up for photo tags. The photo.tagged rule never fired, so members tagged before the fix were never told. Preview first; the send stays locked until you have seen the roster and typed the count-bound phrase.',
      }}
    >
      <SectionCard title="Notify members tagged before the fix" style={{ maxWidth: 940 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* THIS COPY IS LOAD-BEARING AND WAS ONCE BACKWARDS. It used to say
              "nothing here de-duplicates against a previous run … a second run
              notifies the same members a second time", which is the opposite of
              what the function does: the in-app rows are upserted
              `onConflict: 'dedup_key', ignoreDuplicates: true` on a key of the
              backfill's own (`photo_tag_backfill:v1:<user>`, dedupKeyFor) and
              `.select('dedup_key')` counts only rows actually inserted, while
              the push leg filters the same keys against push_dispatch_log
              before sending. Telling an operator otherwise scares them off the
              documented recovery from a partial failure — re-preview and
              re-run — which leaves the members who failed unnotified forever. */}
          <Alert
            type="info"
            showIcon
            message="Re-running is safe — a second run only fills the gaps"
            description={
              <>
                The <code>photo.tagged</code> rule was enabled but never fired, so this walks the
                existing photo-tag nominations and notifies each tagged member about the photos
                they appear in. The server de-duplicates per member against every earlier run: the
                in-app notification is written on a one-time key of its own and the push is
                filtered against the dispatch log by that same key, so a member who has already
                been told is skipped rather than told twice. That makes another preview and run
                the right recovery when a run reports failures — it retries only what did not
                land. One honest gap: a push that reached Expo but whose dispatch-log row failed
                to write (it appears in the failures below) can be sent a second time.
              </>
            }
          />

          <Space>
            <Button
              onClick={() => call(false)}
              loading={busy === 'preview'}
              disabled={busy !== null}
            >
              Run preview
            </Button>
            <Typography.Text type="secondary">
              Always start here. A preview sends nothing.
            </Typography.Text>
          </Space>

          {busy !== null && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontFamily: brand.fSans,
                fontSize: 13,
                color: brand.sub,
              }}
            >
              <Spin size="small" />
              <span>
                {busy === 'preview'
                  ? 'Scanning photo-tag nominations…'
                  : 'Notifying tagged members…'}
              </span>
            </div>
          )}

          {/* The testids sit on wrapper divs, not on the Alerts: antd's
              AlertProps does not admit data-* attributes, so putting one on the
              component itself is a type error. */}
          {invokeError && (
            <div data-testid="ptb-invoke-error">
              <Alert
                type="error"
                showIcon
                // Deliberately not "nothing was sent": this branch also catches
                // a transport failure mid-run, where what reached members is
                // unknown. The server's own refusals that arrive as non-2xx —
                // the 428 phrase mismatch and the 409 zero-member live run —
                // land here too, and say so in their own words.
                message="The run did not return a result"
                description={invokeError}
              />
            </div>
          )}

          {refusal && (
            <div data-testid="ptb-refusal">
              <Alert
                // A refusal arrives as HTTP 200. `no_recipients` is a dead end
                // to go and investigate; the other two are something switched
                // off or broken, which is an error.
                type={result?.reason === 'no_recipients' ? 'warning' : 'error'}
                showIcon
                message="The run refused — nothing was sent"
                description={refusal}
              />
            </div>
          )}

          {result && busy === null && (
            <div data-testid="ptb-summary">
              <StatStrip stats={stats} columns={4} />

              <Typography.Paragraph
                type="secondary"
                style={{ fontFamily: brand.fSans, fontSize: 12.5, margin: '0 0 8px' }}
              >
                “POSTed to Expo” counts push messages handed to Expo, not messages delivered —
                nothing records push tickets and nothing polls receipts, so a POSTed message can
                still be dropped without a trace.
              </Typography.Paragraph>

              {/* `ran`, i.e. dry_run:false AND ok:true — the shape the
                  function returns once the work is done. The warning variant is
                  reachable precisely because `ok` no longer folds in
                  `failures.length`: a completed run with failures is ok:true. */}
              {ran && (
                <Result
                  status={failures.length > 0 ? 'warning' : 'success'}
                  title={
                    failures.length > 0
                      ? `Finished with ${failures.length} failure${
                          failures.length === 1 ? '' : 's'
                        }`
                      : 'Backfill complete'
                  }
                  subTitle={`${result.notified} notified · ${result.pushed} POSTed to Expo · ${skipped.length} skipped`}
                  style={{ paddingTop: 8 }}
                />
              )}

              {result.note && (
                <Typography.Paragraph
                  type="secondary"
                  style={{ fontFamily: brand.fSans, fontSize: 12.5 }}
                >
                  {result.note}
                </Typography.Paragraph>
              )}

              {sample.length > 0 && (
                <div data-testid="ptb-sample">
                  <Table
                    size="small"
                    rowKey="user_id"
                    pagination={false}
                    dataSource={sample}
                    columns={[
                      { title: 'Member', dataIndex: 'name' },
                      { title: 'Tags', dataIndex: 'tags' },
                      {
                        // The function echoes the SAME `sample` back after a
                        // live run, so the tense has to follow the payload: a
                        // column headed "will be told" over a send that already
                        // happened describes a future that is in the past.
                        title: ran ? 'How they were told' : 'How they will be told',
                        dataIndex: 'has_token',
                        // Shown BEFORE the send, on purpose: a member with no
                        // registered device gets the in-app notification only,
                        // and the operator should not have to infer that
                        // afterwards from a push count lower than the member
                        // count. In prod today that is one of three members.
                        render: (hasToken: boolean, row: BackfillSampleRow) => {
                          // A member in `failures` must not keep a green tag.
                          // After a run this table is a record, and a row
                          // reading "Push + in-app" for a member whose push
                          // came back DeviceNotRegistered is the page asserting
                          // something the payload contradicts two boxes down.
                          if (ran && failedMembers.has(row.user_id)) {
                            return <Tag color="red">Failed — see below</Tag>
                          }
                          if (!hasToken) {
                            return <Tag color="gold">In-app only — no registered device</Tag>
                          }
                          // "POSTed" after the fact, never "delivered": nothing
                          // writes push_tickets and nothing polls receipts.
                          return (
                            <Tag color="green">
                              {ran ? 'Push POSTed + in-app' : 'Push + in-app'}
                            </Tag>
                          )
                        },
                      },
                    ]}
                    title={() =>
                      `Tagged members (${sample.length} of ${result.members})${
                        ran ? ' — as of this run' : ''
                      }`
                    }
                  />
                </div>
              )}

              {skipped.length > 0 && (
                <div data-testid="ptb-skipped" style={{ marginTop: 14 }}>
                  <Table
                    size="small"
                    rowKey={(r) => `${r.user_id}-${r.reason}`}
                    pagination={false}
                    dataSource={skipped}
                    columns={[
                      { title: 'Member', dataIndex: 'user_id' },
                      { title: 'Why they were passed over', dataIndex: 'reason' },
                    ]}
                    title={() => `Skipped (${skipped.length})`}
                  />
                </div>
              )}

              {failures.length > 0 && (
                <div data-testid="ptb-failures" style={{ marginTop: 14 }}>
                  <Alert
                    type="error"
                    showIcon
                    message={failureHeadline}
                    description={
                      <div>
                        {failures.map((f, i) => (
                          <div
                            key={`${f.user_id}-${i}`}
                            style={{ fontFamily: brand.fSans, fontSize: 12.5 }}
                          >
                            {f.user_id ? (
                              <code style={{ color: brand.jaguar }}>{f.user_id}</code>
                            ) : (
                              <strong>The whole run</strong>
                            )}{' '}
                            ({f.step}): {f.error}
                          </div>
                        ))}
                        {stepFailures.length > 0 && (
                          <div style={{ fontFamily: brand.fSans, fontSize: 12.5, marginTop: 6 }}>
                            A “whole run” row is not one member’s problem — that step failed for
                            every member it covered. <code>push</code> means the fan-out itself
                            threw, so nobody was pushed; <code>dispatch_log</code> means the
                            pushes went out but were not recorded, so another run can push those
                            members a second time.
                          </div>
                        )}
                      </div>
                    }
                  />
                </div>
              )}
            </div>
          )}

          {preview !== null && members > 0 && (
            <SectionCard title="Confirm the send">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Typography.Text>
                  Type{' '}
                  <Typography.Text code copyable>
                    {expected}
                  </Typography.Text>{' '}
                  to enable the button. The phrase includes the count, so it stops working the
                  moment the roster changes.
                </Typography.Text>
                <Input
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder={expected}
                  aria-label="Confirmation phrase"
                />
                <Button
                  danger
                  type="primary"
                  disabled={!canRun}
                  loading={busy === 'live'}
                  onClick={runLive}
                >
                  Run the backfill now
                </Button>
              </Space>
            </SectionCard>
          )}
        </Space>
      </SectionCard>
    </ListPage>
  )
}
